import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { staffAPI } from '../services/api';
import toast from '../utils/toast';

const invalidatePassportReturns = (queryClient) =>
  queryClient.invalidateQueries({ queryKey: ['passport-return'] });

const mutationError = (error) =>
  toast.error(error?.response?.data?.error || 'Passport return action failed.');

export const usePassportReturnSummary = (options = {}) =>
  useQuery({
    queryKey: ['passport-return', 'summary'],
    queryFn: async () => {
      const response = await staffAPI.getPassportReturnSummary();
      return response.data;
    },
    staleTime: 60 * 1000,
    ...options,
  });

export const usePassportReturnRecords = (options = {}) =>
  useQuery({
    queryKey: ['passport-return', 'records'],
    queryFn: async () => {
      const response = await staffAPI.getPassportReturnRecords();
      return response.data?.results || response.data || [];
    },
    staleTime: 30 * 1000,
    ...options,
  });

export const usePassportReturnRosterPreview = (options = {}) =>
  useQuery({
    queryKey: ['passport-return', 'roster-preview'],
    queryFn: async () => {
      const response = await staffAPI.getPassportReturnRosterPreview();
      return response.data;
    },
    staleTime: 60 * 1000,
    ...options,
  });

export const usePassportReturnEligibleWorkers = (options = {}) =>
  useQuery({
    queryKey: ['passport-return', 'eligible-workers'],
    queryFn: async () => {
      const response = await staffAPI.getPassportReturnEligibleWorkers();
      return response.data || {};
    },
    staleTime: 2 * 60 * 1000,
    ...options,
  });

export const usePassportReturnHRWitnesses = (options = {}) =>
  useQuery({
    queryKey: ['passport-return', 'hr-witnesses'],
    queryFn: async () => {
      const response = await staffAPI.getPassportReturnHRWitnesses();
      return response.data || [];
    },
    staleTime: 5 * 60 * 1000,
    ...options,
  });

const useReturnMutation = ({ mutationFn, successMessage }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      invalidatePassportReturns(queryClient);
      toast.success(successMessage);
    },
    onError: mutationError,
  });
};

export const useRegisterPassportReturnWorker = () =>
  useReturnMutation({
    mutationFn: (workerStaffId) =>
      staffAPI.registerPassportReturnWorker({ worker_staff_id: workerStaffId }),
    successMessage: 'Worker passport registered.',
  });

export const useSeedPassportReturnRoster = () =>
  useReturnMutation({
    mutationFn: () =>
      staffAPI.seedPassportReturnRoster({ confirm_inventory: true }),
    successMessage: 'Worker roster loaded from current custody inventory.',
  });

export const useLaunchPassportReturnCampaign = () =>
  useReturnMutation({
    mutationFn: () =>
      staffAPI.launchPassportReturnCampaign({ confirm_terms_reviewed: true }),
    successMessage: 'Passport return campaign launched.',
  });

export const useMarkPassportReturnReady = () =>
  useReturnMutation({
    mutationFn: (id) => staffAPI.markPassportReturnReady(id),
    successMessage: 'Passport marked ready for handover.',
  });

export const useMarkPassportReturnHeldReady = () =>
  useReturnMutation({
    mutationFn: () => staffAPI.markPassportReturnHeldReady(),
    successMessage: 'Held passports marked ready. Each worker has been notified to collect.',
  });

export const useHandoverPassportReturn = () =>
  useReturnMutation({
    mutationFn: (id) => staffAPI.handoverPassportReturn(id),
    successMessage: 'Physical passport handover recorded.',
  });

export const useReceivePassportReturnIntoCustody = () =>
  useReturnMutation({
    mutationFn: (id) => staffAPI.receivePassportReturnIntoCustody(id),
    successMessage: 'Passport moved to the HR safe. Acknowledgement is due after handover.',
  });

export const useAcknowledgePassportReturn = () =>
  useReturnMutation({
    mutationFn: ({ id, translationId }) =>
      staffAPI.acknowledgePassportReturn(id, {
        translation_id: translationId,
        receipt_confirmed: true,
        policy_acknowledged: true,
      }),
    successMessage: 'Passport receipt and policy acknowledgement recorded.',
  });

export const useDownloadPassportReturnReceiptPdf = () =>
  useMutation({
    mutationFn: async ({ id, filename }) => {
      const response = await staffAPI.downloadPassportReturnReceiptPdf(id);
      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: 'application/pdf' }),
      );
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    },
    onSuccess: () => toast.success('Acknowledgement receipt downloaded.'),
    onError: mutationError,
  });

export const useRecordPassportReturnException = () =>
  useReturnMutation({
    mutationFn: ({ id, witnessStaffId, reason }) =>
      staffAPI.recordPassportReturnException(id, {
        witness_staff_id: witnessStaffId,
        reason,
      }),
    successMessage: 'Witnessed exception recorded.',
  });
